"use client"

import { useState, useEffect } from "react"
import DashboardTemplate from "../templates/DashboardTemplate"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import Card from "../molecules/Card"
import Button from "../atoms/Button"
import { ClipboardList, CheckCircle, XCircle, Clock, Truck, Coffee } from "lucide-react"

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <svg
      className="animate-spin h-10 w-10 text-amber-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  </div>
)

const StatusBadge = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case "Pending":
        return { icon: Clock, color: "bg-amber-100 text-amber-800 border-amber-200" }
      case "Accepted":
        return { icon: CheckCircle, color: "bg-blue-100 text-blue-800 border-blue-200" }
      case "In Progress":
        return { icon: Coffee, color: "bg-purple-100 text-purple-800 border-purple-200" }
      case "Ready":
        return { icon: ClipboardList, color: "bg-green-100 text-green-800 border-green-200" }
      case "Completed":
        return { icon: CheckCircle, color: "bg-green-100 text-green-800 border-green-200" }
      case "Cancelled":
        return { icon: XCircle, color: "bg-red-100 text-red-800 border-red-200" }
      default:
        return { icon: Clock, color: "bg-gray-100 text-gray-800 border-gray-200" }
    }
  }

  const { icon: Icon, color } = getStatusConfig()

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
      <Icon className="w-3.5 h-3.5 mr-1" />
      {status}
    </span>
  )
}

export default function OrderManagementPage() {
  const { user } = useAuth()
  const ownerId = user?._id
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [modal, setModal] = useState(null)
  const [notification, setNotification] = useState(null)

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data: rest } = await api.get(`/restaurants/owner/${ownerId}`)
        const { data: ords } = await api.get(`/orders/restaurant/${rest._id}`)
        setOrders(ords)
      } catch (err) {
        setError(err.response?.data?.error || err.message)
      } finally {
        setLoading(false)
      }
    }

    if (ownerId) {
      fetchOrders()
    }
  }, [ownerId])

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/orders/${id}`, { status })
      setOrders(orders.map((o) => (o._id === id ? data : o)))
      setModal(null)
      showNotification("success", `Order status updated to ${status}`)
    } catch (err) {
      showNotification("error", err.response?.data?.error || "Failed to update order status")
    }
  }

  const showNotification = (type, message) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }

  if (loading)
    return (
      <DashboardTemplate>
        <Spinner />
      </DashboardTemplate>
    )
  if (error)
    return (
      <DashboardTemplate>
        <div className="p-6">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        </div>
      </DashboardTemplate>
    )

  return (
    <DashboardTemplate>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            <ClipboardList className="inline-block mr-2 text-amber-600" />
            Order Management
          </h1>
          <div className="text-sm text-gray-500">
            {orders.length} {orders.length === 1 ? "order" : "orders"} total
          </div>
        </div>

        {notification && (
          <div
            className={`mb-4 p-3 rounded-md ${
              notification.type === "success"
                ? "bg-green-100 border border-green-300 text-green-800"
                : "bg-red-100 border border-red-300 text-red-800"
            }`}
          >
            {notification.message}
          </div>
        )}

        {orders.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
            <ClipboardList className="mx-auto h-12 w-12 text-amber-500 mb-2" />
            <h3 className="text-lg font-medium text-gray-900">No orders yet</h3>
            <p className="mt-1 text-sm text-gray-500">Orders will appear here once customers place them.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {orders.map((order) => (
              <Card
                key={order._id}
                header={
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-lg">Order #{order._id.slice(0, 8)}</h3>
                    <StatusBadge status={order.status} />
                  </div>
                }
                footer={
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {new Date(order.createdAt || Date.now()).toLocaleString()}
                    </span>
                    <Button onClick={() => setModal(order)} className="bg-amber-600 hover:bg-amber-700 text-white">
                      Update Status
                    </Button>
                  </div>
                }
                className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="space-y-2">
                  <p className="flex items-center">
                    <span className="font-medium mr-2">Customer:</span>
                    {order.user?.username || "Anonymous"}
                  </p>
                  <p className="flex items-center">
                    <span className="font-medium mr-2">Type:</span>
                    {order.orderType === "delivery" ? (
                      <span className="flex items-center">
                        <Truck className="h-4 w-4 mr-1 text-amber-600" /> Delivery
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Coffee className="h-4 w-4 mr-1 text-amber-600" /> Dine-in
                      </span>
                    )}
                  </p>
                  <div>
                    <span className="font-medium">Items:</span>
                    <ul className="mt-1 space-y-1">
                      {order.orderItems.map((oi, idx) => (
                        <li key={idx} className="text-sm pl-2 border-l-2 border-amber-200">
                          {oi.item.name} × {oi.quantity}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {modal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
              <h2 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Update Order Status</h2>
              <p className="mb-4 text-gray-600">Order #{modal._id.slice(0, 8)}</p>

              <div className="mb-4">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  id="status"
                  value={modal.status}
                  onChange={(e) => setModal({ ...modal, status: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-amber-500 focus:border-amber-500"
                >
                  {["Pending", "Accepted", "In Progress", "Ready", "Completed", "Cancelled"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button onClick={() => setModal(null)} className="bg-gray-200 hover:bg-gray-300 text-gray-800">
                  Cancel
                </Button>
                <Button
                  onClick={() => updateStatus(modal._id, modal.status)}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardTemplate>
  )
}
